import { useState, useEffect, useCallback, useRef } from 'react';
import { INotificationFetchProps, INotificationApiConfig, ILegacyNotification } from '../models';
import { transformNotifications } from '../utils';
import {
  INotificationCategory,
  NotificationStatusType,
} from '@coherence-design-system/notification-center';
import { getTenantId } from 'src/Common/Models/StoreConstants';

export default function useFetchNotifions(props: INotificationFetchProps & INotificationApiConfig) {
  const {
    apiUrl,
    authToken,
    coreApiToken,
    graphToken,
    email,
    updateNotificationCount,
    onDismissItem,
  } = props;
  const [categories, setCategories] = useState<INotificationCategory[]>([]);
  const [legacyNotifications, setLegacyNotifications] = useState<ILegacyNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const currentTenantID = getTenantId();

  const updateNotificationStatus = useCallback(
    (itemKey: string, displayStatus: NotificationStatusType, readStatus?) => {
      setCategories((prevCategories) =>
        prevCategories.map((category) => {
          const updatedItems = category.items.map((item) => {
            if (item.itemKey === itemKey) {
              return {
                ...item,
                notificationStatus: displayStatus,
                status: readStatus,
              };
            }
            return item;
          });
          return { ...category, items: updatedItems };
        })
      );
    },
    []
  );

  const onDismissItemRef = useRef(onDismissItem);
  onDismissItemRef.current = onDismissItem;

  const handleDismiss = useCallback(
    (itemKey, status) => {
      // Call updateNotificationStatus
      updateNotificationStatus(itemKey, status);

      // Call onUpdateItem
      onDismissItemRef.current(itemKey, status);
    },
    [updateNotificationStatus]
  );

  const categoriesRef = useRef(categories);
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  const getNotificationStatus = (itemKey: string) => {
    const category = categoriesRef.current.find((c) => c.items.some((i) => i.itemKey === itemKey));
    return category?.items.find((i) => i.itemKey === itemKey)?.notificationStatus;
  };

  const fetchNotifications = useCallback(async () => {
    const getNotifications = async () => {
      const response = await fetch(`${apiUrl}?userID=${email}`, {
        method: 'GET',
        headers: {
          Authorization: authToken,
          Coreapitoken: coreApiToken,
          Graphtoken: graphToken,
          'Content-type': 'application/json;',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error. status: ${response.status}`);
      }
      return response;
    };

    setLoading(true);
    try {
      if (email) {
        const response = await getNotifications();
        const data = await response.json();
        let filteredData: ILegacyNotification[] = [];
        filteredData = data.filter((item) => {
          // Get the tag value and normalize it
          const tag = item.tag?.toLowerCase().trim() || '';

          if (currentTenantID) {
            const normalizedTenantId = currentTenantID.toLowerCase();
            return (
              tag.includes(`tenantid=${normalizedTenantId}`) ||
              tag.includes(`tenantid:${normalizedTenantId}`) ||
              (!tag.includes('tenantid=') && !tag.includes('tenantid:'))
            );
          } else {
            return !tag.includes('tenantid=') && !tag.includes('tenantid:');
          }
        });
        let teachingBubble = data.filter(
          (item: { type: string }) => item.type === 'teachingBubble'
        );
        let freNotifications = data.filter(
          (item: { tag: string }) => item.tag?.startsWith('FRE')
        );
        setLegacyNotifications(data);
        // Transform the Legacy Notifications to the new Notification Center format
        // And set the state
        const transformedCategories = transformNotifications(filteredData, handleDismiss);
        setCategories(transformedCategories);
        setLegacyNotifications(filteredData); // Update legacy notifications with filtered data
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, authToken, coreApiToken, email, graphToken, handleDismiss, currentTenantID]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Update the notification count in the header when the categories change
  useEffect(() => {
    updateNotificationCount(categories);
  }, [categories, updateNotificationCount]);

  // Expose a callback function to remove the dismissed notifications
  const handleAfterDismiss = useCallback((notificationIds: string[]) => {
    const filteredData = categoriesRef.current.map((category) => ({
      ...category,
      items: category.items.filter((item) => !notificationIds.includes(item.itemKey)),
    }));
    setCategories(filteredData);

    // update the item status in legacyNotifications as well to keep it in sync
    setLegacyNotifications((prev) =>
      prev.filter((item) => !notificationIds.includes(`${item.id}`))
    );
  }, []);

  const getAllNotificationIds = useCallback(() => {
    const collectedItemKeys = categories.reduce((acc, category) => {
      const itemKeys = category.items.map((item) => item.itemKey);
      return [...acc, ...itemKeys];
    }, []);

    return collectedItemKeys;
  }, [categories]);

  return {
    categories,
    loading,
    error,
    handleAfterDismiss,
    getAllNotificationIds,
    updateNotificationStatus,
    getNotificationStatus,
    legacyNotifications,
  };
}
